import type { CreateThirdPartyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { CreateThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { InsertThirdPartyInput } from '../nativeMutationInputs';
import type { FrontendRouterInputs } from '../routerInputs';
import {
  toCriticality,
  toThirdPartyStatus,
  toThirdPartyType,
} from './thirdPartyEnums';

export type InsertThirdPartyTRPCInput =
  FrontendRouterInputs['thirdParty']['insert'];

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): CreateThirdPartyMutation => {
  return {
    insertThirdPartyApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertThirdParty = createMutationHook({
  name: 'insertThirdParty',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.thirdParty.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (variables: InsertThirdPartyInput): InsertThirdPartyTRPCInput => ({
    ...variables,
    Type: toThirdPartyType(variables.Type),
    Status: toThirdPartyStatus(variables.Status),
    Criticality: toCriticality(variables.Criticality),
  }),
  invalidate: (trpc) => [
    trpc.frontend.thirdParty.register.queryKey(),
    trpc.frontend.thirdParty.getById.queryKey(),
  ],
  mapResponse: (result: { Id: string }): CreateThirdPartyMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: CreateThirdPartyDocument,
  graphqlVariables: (variables: InsertThirdPartyInput) => ({
    object: variables,
  }),
  evictFields: ['third_party'],
});
